import { generateHtmlTagsWithScriptUrl } from '@botpress/webchat-generator'
import { PhotoIcon as PhotoIcon2 } from '@heroicons/react/20/solid'
import { PhotoIcon } from '@heroicons/react/24/outline'
import { useEffect, useMemo, useState } from 'react'
import { TwitterPicker } from 'react-color'
import { FiExternalLink } from 'react-icons/fi'
import { HiOutlineEnvelope, HiOutlineGlobeAlt, HiOutlinePhone, HiChevronDown, HiChevronUp } from 'react-icons/hi2'
import { CodeSnippet } from '../../../../../components'
import { Input, Link, Popover, Select, SelectItem, Separator, Textarea } from '../../../../../elements'
import { config } from '../../../../../shared'
import { AvatarForm } from './AvatarForm'
import type { WebchatFormProps } from './WebchatForm'

import {
  tomato,
  red,
  ruby,
  crimson,
  pink,
  plum,
  purple,
  violet,
  iris,
  indigo,
  blue,
  cyan,
  teal,
  jade,
  green,
  grass,
  orange,
  amber,
  yellow,
  lime,
} from '@radix-ui/colors'
import { Callout, Button } from '~/elementsv2'
import { getQueryOptions, showCustomDialog } from '~/services'
import { Flex } from '@radix-ui/themes'
import { DialogFooter } from '~/componentsV2'
import { useQuery } from '@tanstack/react-query'

export const baseColors = {
  tomato,
  red,
  ruby,
  crimson,
  pink,
  plum,
  purple,
  violet,
  iris,
  indigo,
  blue,
  cyan,
  teal,
  jade,
  green,
  grass,
  orange,
  amber,
  yellow,
  lime,
} as const

const pickerColors = Object.values(baseColors).map((palette) => Object.values(palette)[8] ?? '')

function AvatarFormDialog({
  updateFormData,
  avatarUrl,
}: {
  avatarUrl: string
  updateFormData: React.Dispatch<React.SetStateAction<Record<string, any>>>
}) {
  const [newAvatarUrl, setNewAvatarUrl] = useState<string>(avatarUrl ?? '')

  return (
    <Flex className="w-full" direction={'column'}>
      <AvatarForm avatarUrl={newAvatarUrl} setAvatarUrl={setNewAvatarUrl} />
      <DialogFooter
        onConfirm={() => {
          updateFormData((prevData) => ({ ...prevData, avatarUrl: newAvatarUrl }))
        }}
      />
    </Flex>
  )
}

export function WebchatV2Form({ data, setData, webhookUrl, workspaceId }: WebchatFormProps) {
  const [webhookId, setWebhookId] = useState('')
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false)

  const workspace = useQuery({
    ...getQueryOptions('workspaces_/$workspaceId_', { workspaceId }),
    queryKey: [],
    gcTime: 0,
    staleTime: 0,
  }).data

  const embedScripts = useMemo(
    () =>
      generateHtmlTagsWithScriptUrl({
        injectUrl: `${config.cdnBaseUrl}/webchat/v2/inject.js`,
        scriptUrl: `${config.mediaBaseUrl}/${data.botId}/webchat/v2/config.js`,
      }),
    [data]
  )

  useEffect(() => {
    if (webhookUrl) {
      const webhookIdentifier = webhookUrl.split('/').pop()

      if (webhookIdentifier) {
        setWebhookId(webhookIdentifier)
      }
    }
  }, [webhookUrl])

  const shareableLink = useMemo(() => `${config.cdnBaseUrl}/webchat/v2/shareable.html?botId=${data.botId}`, [data])
  return (
    <>
      <h3 className="mb-2 text-base font-medium text-gray-12">Settings</h3>
      <p className="mb-6 flex text-xs font-light text-gray-11">Configuration for the webchat</p>
      <div className="grid grid-cols-[1fr,auto,1fr] gap-6">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <div
              className="group relative cursor-pointer"
              onMouseDown={() =>
                showCustomDialog({
                  content: <AvatarFormDialog avatarUrl={data.avatarUrl} updateFormData={setData} />,
                })
              }
            >
              <div className="bg-gray-12/70 absolute inset-0 z-10 flex items-center justify-center rounded-full opacity-0 transition-opacity group-hover:opacity-100">
                <PhotoIcon className="aspect-square h-5 text-gray-12" />
              </div>
              {data.avatarUrl ? (
                <img
                  className="aspect-square h-11 max-w-none rounded-full object-cover"
                  src={data.avatarUrl}
                  alt="Bot Avatar"
                />
              ) : (
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gray-1">
                  <PhotoIcon2 className="h-5 w-5 text-gray-11" />
                </div>
              )}
            </div>

            <Input
              placeholder="Bot name"
              value={data.botName ?? ''}
              onChange={(e) => setData((prevData) => ({ ...prevData, botName: e.target.value }))}
            />
          </div>
          <Textarea
            label="Bot description"
            className="resize-none"
            placeholder="This is an amazing bot"
            value={data.botConversationDescription ?? ''}
            onChange={(e) => setData((prevData) => ({ ...prevData, botConversationDescription: e.target.value }))}
          />
          <Input
            label="Composer placeholder"
            placeholder={'Chat with bot'}
            value={data.composerPlaceholder ?? ''}
            onChange={(e) => setData((prevData) => ({ ...prevData, composerPlaceholder: e.target.value }))}
          />
          <div className="flex items-end gap-3">
            <Select
              value={data.theme}
              onValueChange={(value) => setData((prevData) => ({ ...prevData, theme: value }))}
              label="Theme"
              placeholder="Select a Theme..."
              sideOffset={8}
            >
              <SelectItem value="prism">Prism</SelectItem>
              <SelectItem value="galaxy">Galaxy</SelectItem>
              <SelectItem value="dusk">Dusk</SelectItem>
              <SelectItem value="eggplant">Eggplant</SelectItem>
              <SelectItem value="dawn">Dawn</SelectItem>
              <SelectItem value="midnight">Midnight</SelectItem>
            </Select>
            <Popover>
              <Popover.Trigger>
                <div className="mb-1 h-7 w-7 rounded border" style={{ background: data.themeColor }} />
              </Popover.Trigger>
              <Popover.Content className="flex flex-col gap-4 p-0" asChild>
                <TwitterPicker
                  width="325px"
                  color={data.themeColor}
                  colors={pickerColors}
                  onChange={(color) => setData((prevData) => ({ ...prevData, themeColor: color.hex }))}
                />
              </Popover.Content>
            </Popover>
          </div>
        </div>
        <Separator orientation="vertical" />
        <div className="flex flex-col gap-4">
          <Input
            leadingIcon={<HiOutlineGlobeAlt className="mr-1 h-6 w-6 text-gray-9" />}
            placeholder="https://example.com"
            value={data.website ?? ''}
            onChange={(e) => setData((prevData) => ({ ...prevData, website: e.target.value }))}
          />
          <Input
            leadingIcon={<HiOutlinePhone className="mr-1 h-6 w-6 text-gray-9" />}
            placeholder="+1 555 555 5555"
            value={data.phoneNumber ?? ''}
            onChange={(e) => setData((prevData) => ({ ...prevData, phoneNumber: e.target.value }))}
          />
          <Input
            leadingIcon={<HiOutlineEnvelope className="mr-1 h-6 w-6 text-gray-9" />}
            placeholder="example@example.com"
            value={data.emailAddress ?? ''}
            onChange={(e) => setData((prevData) => ({ ...prevData, emailAddress: e.target.value }))}
          />
          <Input
            label="Terms & Conditions"
            placeholder="https://example.com/terms"
            value={data.termsConditions ?? ''}
            onChange={(e) => setData((prevData) => ({ ...prevData, termsConditions: e.target.value }))}
          />
          <Input
            label="Privacy Policy"
            placeholder="https://example.com/privacy"
            value={data.privacyPolicy ?? ''}
            onChange={(e) => setData((prevData) => ({ ...prevData, privacyPolicy: e.target.value }))}
          />
        </div>
      </div>
      <div className="mt-10 max-w-2xl overflow-hidden">
        <h3 className="mb-2 text-base font-medium text-gray-12">Shareable link</h3>
        <p className="mb-3 text-xs font-light text-gray-11">
          Share the following link with people that would like to quickly test your chatbot.
          <Link className="ml-1 inline-flex items-center gap-1" href={shareableLink} target="_blank">
            View
            <FiExternalLink />
          </Link>
        </p>
        <CodeSnippet code={shareableLink} />
      </div>
      <div className="mt-10 max-w-2xl overflow-hidden">
        <h3 className="mb-2 text-base font-medium text-gray-12">Embed code</h3>
        <p className="mb-3 flex text-xs font-light text-gray-11">
          Copy and paste this code on your webpage.
          <Link
            className="ml-1 inline-flex items-center gap-1"
            href="https://botpress.com/docs/developers/webchat-v2/embedded/"
            target="_blank"
          >
            Documentation
            <FiExternalLink />
          </Link>
        </p>
        <CodeSnippet code={embedScripts} />
      </div>
      <div className="mt-4 flex justify-center">
        <Button variant="minimal" onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}>
          {showAdvancedSettings ? (
            <>
              <HiChevronUp className="mr-1" /> Hide Advanced Settings <HiChevronUp className="ml-1" />
            </>
          ) : (
            <>
              <HiChevronDown className="mr-1" /> Show Advanced Settings <HiChevronDown className="ml-1" />
            </>
          )}
        </Button>
      </div>
      {showAdvancedSettings && (
        <div>
          <div className=" max-w-2xl">
            <h3 className="mb-2 text-base font-medium text-gray-12">Client Id</h3>
            <p className="mb-3 flex text-xs font-light text-gray-11">
              Client id is used to identify the client in the webchat. Only use this if you want to use the client
              without the webchat interface.
              <Link
                className="ml-1 inline-flex items-center gap-1"
                href="https://botpress.com/docs/developers/webchat-v2/javascript-client/"
                target="_blank"
              >
                Documentation
                <FiExternalLink />
              </Link>
            </p>
            {webhookId && <CodeSnippet code={webhookId} />}
          </div>

          <div className="mt-4">
            <Textarea
              label="Additional Styles (CSS)"
              rows={6}
              placeholder={`.bpContainer {\n  border: 1px solid #0090FF;\n}`}
              value={data.additionalStyles ?? ''}
              onChange={(e) => setData((prevData) => ({ ...prevData, additionalStyles: e.target.value }))}
            />
          </div>

          <div className="mt-4">
            <h3 className="mb-2 text-base font-medium text-gray-12">Allowed Origins</h3>
            <p className="mb-3 text-xs font-light text-gray-11">
              List of origins that are allowed to access the webchat. Leave an empty list to allow all origins.
            </p>
            <Callout className="mb-2" color="amber">
              You must include <b>{config.dashboardUrl}</b> to still be able to use the chat tab in the dashboard.
            </Callout>
            <div className="mb-4 flex flex-col gap-2">
              {data.allowedOrigins?.map((origin: string, index: number) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    placeholder="http://example.com"
                    value={origin}
                    onChange={(e) => {
                      const newOrigins = [...data.allowedOrigins]
                      newOrigins[index] = e.target.value
                      setData((prevData) => ({ ...prevData, allowedOrigins: newOrigins }))
                    }}
                  />
                  <Button
                    variant="outline"
                    color="red"
                    onClick={() => {
                      const newOrigins = [...data.allowedOrigins]
                      newOrigins.splice(index, 1)
                      setData((prevData) => ({ ...prevData, allowedOrigins: newOrigins }))
                    }}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex justify-center">
              {workspace?.plan === 'community' && (
                <Callout className="text-center">
                  <div className="mb-3">This feature is only available for Team plans and above.</div>
                  <Link href={`/workspaces/${workspaceId}/settings/billing/plans`}>
                    <Button variant="soft" size={'1'}>
                      Upgrade Now
                    </Button>
                  </Link>
                </Callout>
              )}

              {workspace && workspace.plan !== 'community' && (
                <Button
                  variant="solid"
                  onClick={() =>
                    setData((prevData) => ({ ...prevData, allowedOrigins: [...prevData.allowedOrigins, ''] }))
                  }
                >
                  Add Origin
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
